import { useEffect, useRef, useState } from "react";
import { Sparkle, Loader2, ExternalLink, Instagram, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TYPE_LABEL } from "@/lib/constants";
import { FileUpload, resolveMediaUrl } from "@/components/FileUpload";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

const cls = "w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-fuchsia-500/30 focus:border-fuchsia-400";

// Lean "curate a partner post/story" dialog: paste IG link + optional screenshot + note -> creates an idea.
export function CurateDialog({ accountKey, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [form, setForm] = useState(initial());
  const [previewBusy, setPreviewBusy] = useState(false);
  const previewedFor = useRef("");
  const f = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  function initial() {
    return { link: "", handle: "", note: "", content_type: "story", media_url: "", caption: "", auto_preview: false };
  }

  useEffect(() => {
    const link = form.link.trim();
    if (!/instagram\.com\/(p|reel|reels|tv)\//.test(link) || link === previewedFor.current) return;
    if (form.media_url && !form.auto_preview) return; // manueller Screenshot hat Vorrang
    const t = setTimeout(async () => {
      previewedFor.current = link;
      setPreviewBusy(true);
      try {
        const { data } = await api.get("/meta/preview-link", { params: { url: link } });
        setForm((s) => ({
          ...s,
          media_url: s.media_url && !s.auto_preview ? s.media_url : data.media_url,
          auto_preview: true,
          handle: s.handle || data.author || "",
        }));
        toast.success("Instagram-Vorschau geladen ✨");
      } catch {
        toast.info("Keine Vorschau für diesen Link verfügbar — Screenshot hochladen");
      } finally {
        setPreviewBusy(false);
      }
    }, 700);
    return () => clearTimeout(t);
  }, [form.link]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateCaption = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/repost-caption", {
        account_key: accountKey, handle: form.handle, note: form.note, content_type: form.content_type,
      });
      f("caption", data.caption || "");
      toast.success("KI-Repost-Caption eingefügt ✨");
    } catch (e) {
      toast.error(e.response?.data?.detail || "KI-Caption fehlgeschlagen");
    } finally {
      setAiBusy(false);
    }
  };

  const submit = async () => {
    if (!form.link.trim() && !form.media_url) {
      return toast.error("Bitte einen Instagram-Link einfügen oder einen Screenshot hochladen");
    }
    const handle = form.handle.trim().replace(/^@/, "");
    const title = handle ? `Repost: @${handle}` : "Kuratierter Beitrag";
    const caption = form.caption.trim()
      || [handle ? `Repost von @${handle}` : "", form.note.trim()].filter(Boolean).join(" — ");
    setSaving(true);
    try {
      await api.post("/content", {
        account_key: accountKey,
        title,
        content_type: form.content_type,
        status: "idea",
        caption,
        source_link: form.link.trim(),
        media_url: form.media_url,
      });
      toast.success("Als Idee in die Pipeline übernommen ✨");
      setOpen(false);
      setForm(initial());
      onCreated?.();
    } catch {
      toast.error("Übernehmen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="curate-btn"
          className="inline-flex items-center gap-2 border border-neutral-200 text-neutral-800 text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-neutral-50 transition"
        >
          <Sparkle className="h-4 w-4 text-fuchsia-500" /> Kuratieren
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <Instagram className="h-5 w-5 text-fuchsia-500" /> Beitrag kuratieren
          </DialogTitle>
          <DialogDescription>
            Guten Beitrag oder gute Story eines Vereins/Betriebs gesehen? Link einfügen oder Screenshot hochladen — landet direkt als Idee in der Pipeline.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5">
          <Field label="Instagram-Link (öffentlicher Beitrag / Reel)">
            <div className="flex gap-2">
              <input
                data-testid="curate-link"
                value={form.link}
                onChange={(e) => f("link", e.target.value)}
                placeholder="https://www.instagram.com/p/…"
                className={cls}
              />
              {form.link && (
                <a href={form.link} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center px-3 rounded-lg border border-neutral-200 hover:bg-neutral-50">
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
            {previewBusy && (
              <div data-testid="curate-preview-loading" className="mt-1.5 flex items-center gap-1.5 text-[12px] text-fuchsia-600">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Vorschau wird geladen…
              </div>
            )}
          </Field>

          <div className="rounded-lg border border-neutral-200 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Screenshot (optional)</span>
              <FileUpload accept="image/*" label="Screenshot hochladen" onUploaded={(d) => setForm((s) => ({ ...s, media_url: d.url, auto_preview: false }))} />
            </div>
            {form.media_url && (
              <div className="mt-2 rounded-md overflow-hidden border border-neutral-200 relative">
                <img data-testid="curate-preview-img" src={resolveMediaUrl(form.media_url)} alt="" className="w-full max-h-48 object-cover" />
                {form.auto_preview && (
                  <span className="absolute top-1.5 left-1.5 bg-fuchsia-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                    Auto-Vorschau
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="@Handle (Quelle)">
              <input
                data-testid="curate-handle"
                value={form.handle}
                onChange={(e) => f("handle", e.target.value)}
                placeholder="@verein_xy"
                className={cls}
              />
            </Field>
            <Field label="Format">
              <select data-testid="curate-type" value={form.content_type} onChange={(e) => f("content_type", e.target.value)} className={cls}>
                {["story", "post", "reel"].map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Notiz (optional)">
            <textarea
              data-testid="curate-note"
              rows={2}
              value={form.note}
              onChange={(e) => f("note", e.target.value)}
              placeholder="z. B. Sommerfest teilen, mit Credit reposten"
              className={`${cls} h-auto py-2 resize-none`}
            />
          </Field>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-neutral-600">Repost-Caption</label>
              <button
                type="button"
                data-testid="curate-ai-caption"
                onClick={generateCaption}
                disabled={aiBusy}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-fuchsia-700 hover:text-fuchsia-800 disabled:opacity-50"
              >
                {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} KI-Caption vorschlagen
              </button>
            </div>
            <textarea
              data-testid="curate-caption"
              rows={3}
              value={form.caption}
              onChange={(e) => f("caption", e.target.value)}
              placeholder="Leer lassen für automatischen Credit — oder KI-Vorschlag generieren."
              className={`mt-1 ${cls} h-auto py-2 resize-none`}
            />
          </div>
        </div>

        <DialogFooter>
          <button
            data-testid="curate-save-btn"
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-neutral-800 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Als Idee übernehmen
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-neutral-600">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
